import { useState } from "react"
import { Alert, Input, Modal, Skeleton, Typography } from "antd"
import { useQuery } from "@tanstack/react-query"
import { purgePreviewSchema, type TenantSummary } from "@csbi/contracts"
import { formatTimestamp } from "@csbi/viz-core"
import { fetchPurgePreview } from "../../features/tiles/api.js"

export type PurgeDialogProps = {
  tenant: TenantSummary | null
  onCancel: () => void
  onConfirm: (confirm: string) => Promise<unknown>
}

export function PurgeDialog({ tenant, onCancel, onConfirm }: PurgeDialogProps) {
  const [typed, setTyped] = useState("")
  const [purging, setPurging] = useState(false)

  const { data: raw, isLoading } = useQuery({
    queryKey: ["purge-preview", tenant?.id],
    queryFn: ({ signal }) => fetchPurgePreview(tenant!.id, signal),
    enabled: Boolean(tenant),
    // Always re-read: the numbers are the point, and a cached count from ten minutes ago would understate a live collection
    staleTime: 0,
  })

  /**
   * ⚠ **Validated here, unlike every other response in the app.**
   */
  const parsed = raw ? purgePreviewSchema.safeParse(raw) : null
  const preview = parsed?.success ? parsed.data : null
  const unreadable = Boolean(raw) && !preview

  const matches = typed === tenant?.id

  return (
    <Modal
      open={Boolean(tenant)}
      title={tenant ? `Purge ${tenant.displayName}?` : ""}
      okText="Purge permanently"
      okButtonProps={{ danger: true, disabled: !matches || isLoading || !preview, loading: purging }}
      onCancel={onCancel}
      onOk={async () => {
        setPurging(true)
        try {
          await onConfirm(typed)
        } finally {
          setPurging(false)
        }
      }}
      destroyOnHidden
    >
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message="This cannot be undone, and there is no backup."
        description="If the customer has simply left, use Offboard instead — it keeps every collected row and stays reversible."
      />

      {unreadable ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not read what this would destroy"
          description="The purge is blocked until it can be shown. Reload the page and try again; if it persists, this deployment has to be purged by hand."
        />
      ) : isLoading || !preview ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        <>
          <p style={{ marginTop: 0 }}>This destroys:</p>
          <ul style={{ paddingInlineStart: 20, margin: "0 0 16px", lineHeight: 1.8 }}>
            <li>
              <strong>{preview.facts.toLocaleString()}</strong> collected data points across{" "}
              <strong>{preview.metrics}</strong> metrics
            </li>
            <li>
              {preview.firstAt && preview.lastAt ? (
                <>
                  everything from <strong>{formatTimestamp(preview.firstAt)}</strong> to{" "}
                  <strong>{formatTimestamp(preview.lastAt)}</strong>
                </>
              ) : (
                "no collected history — nothing has ever been collected from this deployment"
              )}
            </li>
            <li>
              <strong>{preview.collectionTasks.toLocaleString()}</strong> collection log entries, and the registry
              entry itself
            </li>
          </ul>

          {preview.tilesReferencing > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${preview.tilesReferencing} saved dashboard tile${preview.tilesReferencing === 1 ? "" : "s"} name this deployment`}
              description="They will not report an error afterwards — they will simply show nothing."
            />
          )}
        </>
      )}

      <label htmlFor="purge-confirm" style={{ display: "block", marginBottom: 6, fontSize: 13 }}>
        Type <Typography.Text code>{tenant?.id}</Typography.Text> to confirm
      </label>
      <Input
        id="purge-confirm"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={tenant?.id}
        autoComplete="off"
        status={typed && !matches ? "error" : undefined}
      />
    </Modal>
  )
}
